/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useLoanContext } from '../../contexts/LoanContext';
import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, getErrorFromEndpoint } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import { AxiosError } from 'axios';

type BorrowerRecipient = {
  applicationNumber: string;
  borrowerNumber: string;
  signer: boolean;
  routingOrder: number;
  recipientType: string;
  customEmailTemplateName?: string;
};

type BorrowerRecipients = Array<BorrowerRecipient>;

type AISignSendRequest = {
  borrowerRecipients: BorrowerRecipients;
  docId: string;
  imagingFormName?: string;
};

type UnknownAISignSendResp = {
  body?: string;
  subject?: string;
  error?: string;
} | null;

const useAISignSend = () => {
  const { token } = useAuthContext();
  const { get } = useAbortController();
  const { loanNumber } = useLoanContext();
  const [aiSignSendResp, setAISignSendResp] = useState(null);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const handleError = useCallback((res: Error | AxiosError | any) => {
    const err = getErrorFromEndpoint(res);
    if (isStrEmptyOrNull(err)) return '';
    console.log('Error in goGreenEmailAPI in useGoGreenEmails hook: ' + err);
    setErrors(err);
    return err;
  }, []);

  const AISignSendAPI = useCallback(
    async (emailParams: AISignSendRequest): Promise<UnknownAISignSendResp> => {
      if (!token || !loanNumber || isObjEmptyOrNull(emailParams)) return null;
      var urlPath = process.env.REACT_APP_AISIGN_SEND.replace('?', loanNumber);
      let req = { url: urlPath, method: 'POST', data: emailParams };
      let hdrs = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const conf = get();
      //console.log('req', request);
      const res = await axiosCall(req, conf, hdrs)
        .then((res) => {
          const err = handleError(res);
          if (isObjEmptyOrNull(res) || !isStrEmptyOrNull(err)) {
            return null;
          }
          return res;
        })
        .catch((e: AxiosError | Error | any) => {
          handleError(e);
          return null;
        });
      setAISignSendResp(res);
      return res;
    },
    [get, token, handleError, loanNumber],
  );

  const sendAISign = useCallback(
    async (emailParams: AISignSendRequest | null): Promise<UnknownAISignSendResp> => {
      setLoading(true);
      setAISignSendResp(null);
      const resp = await AISignSendAPI(emailParams).finally(() => {
        setLoading(false);
      });
      return resp;
    },
    [AISignSendAPI],
  );

  return { aiSignSendResp, loading, errors, sendAISign };
};

export default useAISignSend;
